import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  Check,
  Clock,
  Cpu,
  FileText,
  Loader2,
  Settings,
  Shield,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import ConfirmDialog from "../StandardsLibrary/ConfirmDialog";
import { listStandards } from "../StandardsLibrary/api";
import { VerityStandard } from "../StandardsLibrary/types";
import {
  RunProgress,
  complianceProgress,
  getProject,
  listDeliverables,
  runCompliance,
  stopCompliance,
} from "./api";
import { ComplianceRun, ProjectDeliverable, VerityProject } from "./types";

interface ComplianceEngineProps {
  project: VerityProject;
  /** The run held by the workspace, so leaving this stage and coming back does not lose it. */
  run: ComplianceRun | null;
  /** Bubbles a completed run up so the header status can flip to Reviewed without a reload. */
  onRun?: (run: ComplianceRun) => void;
  /** Jump to the Findings stage — the run's own "view them all" route. */
  onViewFindings?: () => void;
  /**
   * Watching, not working.
   *
   * An administrator follows a project; the reviewer assigned to it runs the check. The selection
   * panels still show what was chosen — a run in flight is worth watching, and a finished one is
   * worth reading — but nothing here starts or stops one.
   */
  readOnly?: boolean;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString();
}

/**
 * Named stages, so the bar restarting reads as the next one starting rather than as a glitch.
 *
 * Each stage counts its own work from zero — planning counts every requirement, reading counts
 * notes, checking counts requirements again — so without a name on it, the bar reaching the end and
 * dropping back looks like the run lost its place.
 */
const STAGE_LABEL: Record<string, string> = {
  planning: "Identifying requirements",
  reading: "Reading the values stated on the drawings",
  checking: "Checking against the drawings",
  done: "Finished",
};

/**
 * "Step 2 of 3 · Reading the values stated on the drawings".
 *
 * The numbering comes from the server because the stages are not fixed — a standard with no
 * threshold requirements in it never reads declared values, and a screen that hard-coded "of 3"
 * would be wrong on exactly those runs. Falls back to the bare label when the server does not say,
 * which is what an older run in flight will do.
 */
function stageLabel(progress: RunProgress): string {
  const label = STAGE_LABEL[progress.stage] ?? progress.stage;
  if (progress.stage === "done" || !progress.stageIndex || !progress.stageCount) return label;
  return `Step ${progress.stageIndex} of ${progress.stageCount} · ${label}`;
}

/** How long the run took. Rounded: nobody needs the seconds on a forty-minute job. */
function formatDuration(ms: number): string {
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return `${Math.max(1, Math.round(ms / 1000))} sec`;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hr ${minutes % 60} min`;
}

/** Rounded, never precise: "about 4 min" is honest about an estimate in a way "4:07" is not. */
function formatEta(seconds: number): string {
  // CAPPED AT 55, NEVER 60. Rounding to the nearest five turned 59 seconds into "60 sec", so the
  // countdown read "1 min" and then "60 sec" — the same length twice, in two different units, which
  // looks like a fault rather than a tick.
  if (seconds < 60) return `${Math.min(55, Math.max(5, Math.round(seconds / 5) * 5))} sec`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
}

/** How often the screen asks the server where the run has got to. */
const POLL_MS = 2000;

/**
 * The Hybrid Compliance Engine.
 *
 * Pick the deliverables and the standards, run the check, read the findings. Two engines are named
 * up front because the distinction matters to whoever reads the result: a deterministic finding is a
 * rule that failed and carries no doubt, an AI-assisted one is a judgement and carries a confidence.
 *
 * BOTH PANELS ARE REAL. The deliverables are this project's own, filtered to the ones that have
 * actually been read; the standards are whatever the Standards Library holds. Selecting is the
 * useful half of this screen and it works.
 *
 * THE RUN IS REAL AND SLOW. A whole standard is hundreds of model calls, so the screen polls the
 * server for progress rather than leaving a spinner to stand in for ten minutes of silence. What it
 * shows is counted work — rules judged out of rules to judge — not a timer pretending to be one.
 */
const ComplianceEngine = ({
  project,
  run: initialRun,
  onRun,
  onViewFindings,
  readOnly = false,
}: ComplianceEngineProps) => {
  const [deliverables, setDeliverables] = useState<ProjectDeliverable[] | null>(null);
  const [standards, setStandards] = useState<VerityStandard[]>([]);
  const [pickedDocs, setPickedDocs] = useState<string[]>([]);
  const [pickedStds, setPickedStds] = useState<string[]>([]);
  const [run, setRun] = useState<ComplianceRun | null>(initialRun ?? project.complianceRun ?? null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Server-reported progress while a check runs. Null when nothing is running. */
  const [progress, setProgress] = useState<RunProgress | null>(null);
  /** A stop has been asked for; the engine finishes its in-flight calls and then gives up. */
  const [stopping, setStopping] = useState(false);
  /**
   * Watching a run this screen did not start.
   *
   * The distinction matters for how the result arrives. A run this screen started is awaited, and
   * the response carries the findings. A run it merely found in flight has its response going to a
   * page that no longer exists — so this screen learns the outcome from the progress record and
   * then reads the saved run off the project.
   */
  const [attached, setAttached] = useState(false);
  /** The confirmation before stopping. Stopping throws away everything the run has found. */
  const [confirmStop, setConfirmStop] = useState(false);
  /** The "what this does" note. Closed by default — it is an explanation, not a heading. */
  const [helpOpen, setHelpOpen] = useState(false);

  /** The pinned stage header, measured so the scroll below does not park the results underneath it. */
  const headRef = useRef<HTMLDivElement>(null);
  /** The results card, scrolled to when a run starts. */
  const resultsRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      // Only parsed deliverables can be checked — there is nothing to check in an unread file.
      const [docs, stds] = await Promise.all([listDeliverables(project.id), listStandards()]);
      setDeliverables(docs.filter((d) => d.extraction));
      // Engineering deliverables are checked against engineering standards only; AWP standards
      // belong to the other side of the mode switch.
      setStandards(stds.filter((s) => s.reviewType !== "awp"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the inputs.");
      setDeliverables([]);
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Pick up a check that is already running.
   *
   * LEAVING THIS SCREEN DOES NOT STOP A RUN, and nothing about that is going to change — the work
   * happens in the server process, and a check worth ten minutes of model time should survive a
   * click on another tab. What used to happen instead was that the run carried on invisibly: the
   * component unmounted, its poll died with it, and there was no way back to a check that was still
   * spending money. The only honest reading of "still running" is to show it.
   *
   * Also covers a hard refresh, which is the same situation arrived at more abruptly.
   *
   * A run already finished is deliberately NOT adopted — its result is saved, and the run the
   * workspace handed this screen is that same result.
   */
  useEffect(() => {
    let live = true;
    void (async () => {
      const found = await complianceProgress(project.id);
      if (!live || !found || found.stage === "done") return;
      setProgress(found);
      // Put the ticks back. A live bar above two panels of empty checkboxes says a run is happening
      // but refuses to say what it is running against, which is worse than no bar.
      if (found.deliverableIds?.length) setPickedDocs(found.deliverableIds);
      if (found.standardIds?.length) setPickedStds(found.standardIds);
      setAttached(true);
      setRunning(true);
    })();
    return () => {
      live = false;
    };
  }, [project.id]);

  /**
   * Collect the result of a run this screen was only watching.
   *
   * Its response went to a page that is gone, so the outcome comes from the progress record and the
   * findings from the project. A stopped run has nothing to collect — the server does not save one.
   */
  const settleAttached = useCallback(
    async (last: RunProgress | null) => {
      setAttached(false);
      setRunning(false);
      setStopping(false);

      if (last?.outcome === "stopped") {
        setError(
          `Stopped after ${last.rulesDone.toLocaleString()} of ${last.rulesTotal.toLocaleString()} requirements. Nothing was saved.`,
        );
        return;
      }
      // Progress gone with no outcome recorded: the run failed, or the server restarted under it.
      // Reading the project here would surface whatever run was stored BEFORE this one and pass it
      // off as the result, which is worse than saying nothing was produced.
      if (last?.outcome !== "complete") {
        setError("The check ended without producing a result. Run it again.");
        return;
      }

      try {
        const fresh = await getProject(project.id);
        if (fresh.complianceRun) {
          setRun(fresh.complianceRun);
          onRun?.(fresh.complianceRun);
        } else {
          setError("The check finished but its result was not saved. Run it again.");
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "The check finished but its result could not be loaded.",
        );
      }
    },
    [project.id, onRun],
  );

  /**
   * Ask the server where the run has got to, while it is running.
   *
   * Polling rather than streaming: the run is one long HTTP request that cannot also carry updates,
   * and a two-second poll against an in-memory counter is far less machinery than an event stream
   * for something that only needs to move a bar.
   *
   * Cleared on unmount and when the run ends, so a bar can never be left frozen describing work that
   * has finished or a screen nobody is looking at.
   */
  useEffect(() => {
    if (!running) {
      setProgress(null);
      return;
    }
    let live = true;
    const tick = async () => {
      const next = await complianceProgress(project.id);
      if (!live) return;
      setProgress(next);
      // Only a reattached screen ends a run from here. When this screen started the check, the
      // POST's own answer is the authority — it arrives with the findings and cannot be raced.
      if (attached && (next === null || next.stage === "done")) await settleAttached(next);
    };
    void tick();
    const timer = setInterval(() => void tick(), POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [running, attached, settleAttached, project.id]);

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  /**
   * BREACHES ONLY, and no severity anywhere.
   *
   * This screen used to carry six tiles — Findings / Critical / Major / Minor / Deterministic /
   * AI-Assisted — over a table of every finding the run raised. Against a real standard that read
   * "635 findings, 619 minor", which is wrong twice over: 614 of them are readings the engine could
   * not settle rather than defects, and an unsettled reading is filed as minor BY DEFINITION, so
   * the severity axis was measuring nothing. The one number that means anything here is how many
   * things the submission gets wrong.
   *
   * The unresolved count is deliberately NOT shown beside it. "614 to review" promises a queue
   * nobody works through — the same mistake as telling somebody 806 requirements need a person.
   * They are in the run, and reachable from the Findings chips; they are not announced.
   */
  const breaches = useMemo(
    () => (run?.findings ?? []).filter((f) => (f.kind ?? "breach") === "breach").length,
    [run],
  );

  /**
   * A run needs BOTH halves of the comparison.
   *
   * Documents alone is not a check — there is nothing to check them against, and the run would have
   * to either do nothing or quietly pick the standards for you. Lighting the button on the
   * deliverables alone said the opposite.
   */
  const canRun = pickedDocs.length > 0 && pickedStds.length > 0;

  /**
   * Brings the results card into view.
   *
   * The button is at the top of the stage and the results sit below two panels of inputs, so without
   * this a run happens entirely off-screen and pressing the button looks like it did nothing.
   *
   * Scrolls the stage's own scroll container rather than calling scrollIntoView, for one reason: the
   * stage header is sticky, and `block: "start"` parks the card's heading directly underneath it.
   * Measuring the header and stopping short of it puts the card where a reader expects it.
   */
  const revealResults = () => {
    const card = resultsRef.current;
    if (!card) return;
    const region = card.closest(".vr-scroll-region");
    if (!(region instanceof HTMLElement)) {
      card.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const offset = card.getBoundingClientRect().top - region.getBoundingClientRect().top;
    const clearance = (headRef.current?.offsetHeight ?? 0) + 12;
    region.scrollTo({ top: region.scrollTop + offset - clearance, behavior: "smooth" });
  };

  /**
   * Runs the check over what is ticked.
   *
   * The narration is the server's, not a timer's. This used to tick through seven named stages on a
   * 700ms clock and pad the run to five seconds so the theatre had time to play. What replaces it is
   * the engine's own count of what it has actually done — which is worth showing precisely because
   * the real run is long enough to need it.
   */
  const start = async () => {
    if (running || !canRun) return;
    setRunning(true);
    setStopping(false);
    setError(null);
    setRun(null);
    // After the frame that clears the previous run, so the card is measured where it will be and not
    // where it was.
    requestAnimationFrame(revealResults);

    try {
      const result = await runCompliance(project.id, pickedDocs, pickedStds);
      if (result.coverage?.cancelled) {
        // Deliberately NOT shown as a run. A fragment of a check is not a verdict on the
        // submission, and the server does not store it either.
        setError(`Stopped after ${result.coverage.rulesChecked} of ${result.coverage.rulesConsidered} requirements. Nothing was saved.`);
      } else {
        setRun(result);
        onRun?.(result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "The compliance check failed.");
    } finally {
      setRunning(false);
      setStopping(false);
    }
  };

  return (
    <>
      <div className="vr-page-head vr-sub-head" ref={headRef}>
        <div>
          {/* Worth reading once, then in the way — and this header is pinned, so it costs the same
              vertical space on every scroll. Same "i" toggle the other headings use. */}
          <h2 className="vr-h-sec">
            Hybrid Compliance Engine
            <button
              type="button"
              className="vr-info-btn"
              aria-expanded={helpOpen}
              title={helpOpen ? "Hide" : "What this does"}
              onClick={() => setHelpOpen((open) => !open)}
            >
              i
            </button>
          </h2>
          <p hidden={!helpOpen}>
            Cross-check ingested deliverables against your Standards Library — grounded findings with
            clause-level traceability
          </p>
        </div>
        <div className="vr-head-actions">
          {/* Watching a run somebody else started. Said plainly, because a screen with a live
              progress bar and no controls otherwise reads as broken. */}
          {readOnly && (
            <span className="vr-cell-code">
              {running ? "A check is running" : "View only — the assigned reviewer runs checks"}
            </span>
          )}
          {/* Only while running, and beside the disabled Run button rather than replacing it — a
              control that swaps identity under the cursor is how people stop runs by accident. */}
          {!readOnly && running && (
            <button
              className="vr-btn vr-btn-outline"
              onClick={() => setConfirmStop(true)}
              disabled={stopping}
              title={
                stopping
                  ? "Stopping after the calls already in flight"
                  : "Stop this check — what it has found so far is discarded"
              }
            >
              {stopping ? "Stopping…" : "Stop"}
            </button>
          )}
          {!readOnly && (
          <button
            className="vr-btn vr-btn-glow"
            onClick={() => void start()}
            disabled={running || !canRun}
            // A disabled control that does not say why is a dead end. Names the half still missing.
            title={
              canRun
                ? "Check the selected deliverables against the selected standards"
                : pickedDocs.length === 0 && pickedStds.length === 0
                  ? "Select at least one deliverable and one standard"
                  : pickedDocs.length === 0
                    ? "Select at least one deliverable to check"
                    : "Select at least one standard to check against"
            }
          >
            {running ? (
              <>
                <Loader2 size={14} className="vr-spin-inline" /> Running Compliance Check…
              </>
            ) : (
              <>
                <Shield size={14} /> Run Compliance Check
              </>
            )}
          </button>
          )}
        </div>
      </div>

      {/* What the two engines are — part of the same explanation as the heading's note, and behind
          the same ⓘ. It says what "Hybrid" means, which is worth reading once and is then two cards
          of standing text above the controls somebody came here to use.

          Rendered conditionally rather than with `hidden`, because `.vr-grid` sets display:grid and
          a class rule beats the [hidden] attribute — the cards would stay on screen. */}
      {helpOpen && (
        <div className="vr-grid vr-grid-2">
          <GlowCard className="vr-engine-card" hover={false}>
            <div className="vr-card-head">
              <span className="vr-engine-icon">
                <Settings size={14} />
              </span>
              <h3>Deterministic Engine</h3>
            </div>
            <p className="vr-muted">
              Rule-based checks with 100% confidence. Field presence, format validation, cross-reference
              lookups. Pass or fail — no ambiguity.
            </p>
          </GlowCard>

          <GlowCard className="vr-engine-card" hover={false}>
            <div className="vr-card-head">
              <span className="vr-engine-icon vr-engine-ai">
                <Cpu size={14} />
              </span>
              <h3>AI-Assisted Engine</h3>
            </div>
            <p className="vr-muted">
              Semantic and cross-document analysis grounded in the extracted entities. Confidence-scored
              — low confidence routed to manual review.
            </p>
          </GlowCard>
        </div>
      )}

      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-grid vr-grid-2">
        <GlowCard className="vr-pick-card" hover={false}>
          <div className="vr-table-head">
            <div className="vr-card-head">
              <FileText size={14} />
              <h3>
                Deliverables{" "}
                <span className="vr-cell-code">
                  ({pickedDocs.length}/{deliverables?.length ?? 0})
                </span>
              </h3>
            </div>
            {!readOnly && (
            <button
              className="vr-linkbtn"
              onClick={() =>
                setPickedDocs(
                  pickedDocs.length === (deliverables?.length ?? 0)
                    ? []
                    : (deliverables ?? []).map((d) => d.id),
                )
              }
            >
              {pickedDocs.length === (deliverables?.length ?? 0) && pickedDocs.length > 0
                ? "Clear all"
                : "Select all"}
            </button>
            )}
          </div>

          <div className="vr-pick-list">
            {(deliverables ?? []).map((doc) => (
              <label key={doc.id} className="vr-pick-row">
                <input
                  type="checkbox"
                  checked={pickedDocs.includes(doc.id)}
                  disabled={readOnly}
                  onChange={() => setPickedDocs((prev) => toggle(prev, doc.id))}
                />
                <span className="vr-pick-body">
                  <span className="vr-pick-name">{doc.fileName}</span>
                  <span className="vr-pick-meta">
                    {doc.docType}
                    {doc.discipline ? ` · ${doc.discipline}` : ""}
                    {doc.ocrQuality
                      ? ` · OCR: ${doc.ocrQuality[0].toUpperCase()}${doc.ocrQuality.slice(1)}`
                      : ""}
                  </span>
                  {/* Said at the moment of selection, not only on Document AI. Checking a scanned
                      pack is not an error and is sometimes deliberate — but it should never be an
                      accident, because the result comes back almost empty and looks like a pass. */}
                  {doc.drawingSummary?.readability &&
                    doc.drawingSummary.readability.verdict !== "ok" && (
                      <span className="vr-pick-warn">
                        {doc.drawingSummary.readability.verdict === "unreadable"
                          ? "Almost no readable text — a check against this will find little"
                          : "Very little readable text"}
                      </span>
                    )}
                </span>
              </label>
            ))}
            {deliverables !== null && deliverables.length === 0 && (
              <div className="vr-empty">
                Nothing parsed to check. Parse a deliverable on the Document AI step first.
              </div>
            )}
          </div>
        </GlowCard>

        <GlowCard className="vr-pick-card" hover={false}>
          <div className="vr-table-head">
            <div className="vr-card-head">
              <BookOpen size={14} />
              <h3>
                Governing Standards{" "}
                <span className="vr-cell-code">
                  ({pickedStds.length}/{standards.length})
                </span>
              </h3>
            </div>
            {!readOnly && (
            <button
              className="vr-linkbtn"
              onClick={() =>
                setPickedStds(
                  pickedStds.length === standards.length ? [] : standards.map((s) => s.id),
                )
              }
            >
              {pickedStds.length === standards.length && pickedStds.length > 0
                ? "Clear all"
                : "Select all"}
            </button>
            )}
          </div>

          <div className="vr-pick-list">
            {standards.map((standard) => (
              <label key={standard.id} className="vr-pick-row">
                <input
                  type="checkbox"
                  checked={pickedStds.includes(standard.id)}
                  disabled={readOnly}
                  onChange={() => setPickedStds((prev) => toggle(prev, standard.id))}
                />
                <span className="vr-pick-body">
                  <span className="vr-pick-name">{standard.title}</span>
                  <span className="vr-pick-meta">
                    {[standard.domain, standard.authority, standard.edition]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
              </label>
            ))}
            {standards.length === 0 && (
              <div className="vr-empty">
                The Standards Library is empty. Upload a standard to check deliverables against it.
              </div>
            )}
          </div>
        </GlowCard>
      </div>

      <GlowCard className={`vr-table-card${running ? " vr-running" : ""}`} hover={false} ref={resultsRef}>
        <div className="vr-table-head">
          <div className="vr-card-head">
            <Shield size={14} />
            <h3>Compliance Check Results</h3>
          </div>
          <span className="vr-run-stamp">
            {running ? (
              <>
                <Loader2 size={12} className="vr-spin-inline" /> Executing…
              </>
            ) : run ? (
              <>
                <Check size={12} /> Completed {formatTime(run.runAt)}
              </>
            ) : (
              <>
                <Clock size={12} /> No run yet — select inputs and click Run
              </>
            )}
          </span>
        </div>

        {running && (
          <div className="vr-run-log">
            <p className="vr-run-title">
              <Loader2 size={14} className="vr-spin-inline" />{" "}
              {progress?.detail ?? "Starting the compliance engine…"}
            </p>

            {/* Said plainly, because the alternative is a bar appearing on a screen the reader does
                not remember starting anything on. */}
            {attached && (
              <p className="vr-run-note">
                This check was already running — it carried on while you were away.
              </p>
            )}

            {/* A REAL bar, from the server's own count of rules done. The seven fake stages that
                used to sit here ticked on a timer and measured nothing; this moves when work
                happens and stops when it stops. Indeterminate until there is something to divide. */}
            <div className="vr-run-bar" aria-hidden={!progress}>
              <span
                className={`vr-run-bar-fill${progress && progress.rulesTotal > 0 ? "" : " vr-indeterminate"}`}
                style={
                  progress && progress.rulesTotal > 0
                    ? { width: `${Math.min(100, (progress.rulesDone / progress.rulesTotal) * 100)}%` }
                    : undefined
                }
              />
            </div>

            <div className="vr-run-steps">
              {progress && <span>{stageLabel(progress)}</span>}
              {/* The reading stage counts NOTES, not requirements — calling them requirements
                  would have the number mean two different things on the same line. */}
              <span>
                {progress
                  ? `${progress.rulesDone.toLocaleString()} of ${progress.rulesTotal.toLocaleString()} ${
                      progress.stage === "reading" ? "notes" : "requirements"
                    }`
                  : "Preparing…"}
              </span>
              {progress && progress.findings > 0 && (
                <span>
                  {progress.findings} finding{progress.findings === 1 ? "" : "s"} so far
                </span>
              )}
              {/* Absent until the server has seen enough work to mean it, rather than a confident
                  number extrapolated from one batch. */}
              {progress?.etaSeconds != null && progress.etaSeconds > 0 && (
                <span>about {formatEta(progress.etaSeconds)} remaining</span>
              )}
            </div>

            {/* The activity log: what has actually happened, appended as it happens. The screen
                used to show seven invented stages ticking on a timer; these lines are earned. */}
            {progress && progress.steps.length > 0 && (
              <div className="vr-run-trail">
                {progress.steps.map((step) => (
                  <div key={step} className="vr-run-step vr-done">
                    <Check size={13} />
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            )}

            <p className="vr-run-foot">
              Grounded reasoning · Clause-level traceability · Human-in-the-loop review
            </p>
          </div>
        )}

        {!running && !run && (
          <div className="vr-empty">
            {/* Pressing Run scrolls here, so this is where a reader is looking when it fails. Saying
                "No run yet" while the reason sits in a banner off the top of the screen would be
                the screen withholding the one thing it knows. */}
            {error ? (
              <div className="vr-form-error" style={{ margin: 0 }}>
                {error}
              </div>
            ) : (
              <>
                <p>
                  Select deliverables and standards above, then click{" "}
                  <strong className="vr-run-cta">Run Compliance Check</strong>.
                </p>
                <p className="vr-cell-code" style={{ marginTop: ".4rem" }}>
                  New findings will also appear in the Findings Review workflow.
                </p>
              </>
            )}
          </div>
        )}

        {/* THE RECORD OF THE RUN, where the findings table used to be.

            A table of every finding here was the same list the Findings stage owns, rendered worse
            and on the wrong axis — and with 614 of 635 being unsettled readings rather than
            defects, it buried the twenty-one that matter under six hundred that do not. What is
            worth keeping on the screen that RAN the check is what the check did. */}
        {!running && run && (
          <div className="vr-run-log">
            <p className="vr-run-title">
              <Check size={14} /> {breaches} breach{breaches === 1 ? "" : "es"} found
            </p>

            <div className="vr-run-steps">
              <span>
                {run.durationMs
                  ? `Completed in ${formatDuration(run.durationMs)}`
                  : `Completed ${formatTime(run.runAt)}`}
              </span>
              {run.coverage && (
                <span>{run.coverage.rulesChecked.toLocaleString()} requirements checked</span>
              )}
              {run.coverage && <span>{run.coverage.rulesPassed.toLocaleString()} passed</span>}
            </div>

            {/* Said here, not buried in the record: a short findings list without this reads as a
                clean submission.

                STATES THE FACT AND NOTHING MORE. It said "could not be assessed" and that claimed
                an ignorance the engine does not have — most of these are requirements a drawing
                pack is simply not the evidence for, and it knows what they are. But it cannot
                split them honestly either: `ruleType` looked like the axis for that until a rule
                typed `submission` turned out to be "use UTM Zone 17, NAD83 (Original)" and caught
                the wrong datum. So the line reports what happened and leaves the reading alone. */}
            {run.coverage?.rulesNoEvidence ? (
              <p className="vr-run-note">
                {run.coverage.rulesNoEvidence.toLocaleString()} requirements found no matching text
                in the drawings.
              </p>
            ) : null}

            {/* The same lines the activity log showed while it ran. They stop being progress the
                moment it finishes and become the only account of what happened. Absent on runs
                stored before they were kept — the screen simply shows one line less. */}
            {run.steps && run.steps.length > 0 && (
              <div className="vr-run-trail">
                {run.steps.map((step) => (
                  <div key={step} className="vr-run-step vr-done">
                    <Check size={13} />
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            )}

            {/* A run that could not finish a call checked less than it claims. Said here rather
                than left in the coverage record for somebody to go and find. */}
            {run.coverage && run.coverage.failures.length > 0 && (
              <p className="vr-run-note vr-warn">
                {run.coverage.failures.length} model call
                {run.coverage.failures.length === 1 ? "" : "s"} failed — this run is incomplete.
              </p>
            )}

            {onViewFindings && (
              <div className="vr-finding-actions" style={{ marginTop: ".85rem" }}>
                <button className="vr-btn vr-btn-glow vr-btn-sm" onClick={onViewFindings}>
                  Review findings <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}
      </GlowCard>

      {confirmStop && (
        <ConfirmDialog
          title="Stop this compliance check?"
          tone="warn"
          confirmLabel="Stop the check"
          onConfirm={() => {
            setConfirmStop(false);
            setStopping(true);
            void stopCompliance(project.id);
          }}
          onCancel={() => setConfirmStop(false)}
        >
          <p>
            {progress
              ? `It has checked ${progress.rulesDone.toLocaleString()} of ${progress.rulesTotal.toLocaleString()} requirements and raised ${progress.findings}.`
              : "It has only just started."}
          </p>
          <p style={{ marginTop: ".5rem" }}>
            Nothing is kept — a part-finished check is not a verdict on the submission, so it is not
            saved. Starting again begins from the first requirement.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
};

export default ComplianceEngine;
